<style lang="scss">
  @use "../../../../../../svelte-design-system/assets/css/design-system/mixins";
  @use "../../../../../../svelte-design-system/assets/css/design-system/colors";
  @use "../../../../../../svelte-design-system/assets/css/design-system/utils";

  .dialler-controls {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .call-action-buttons {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
      line-height: 1;
      position: relative;
    }

    .end-call-section {
      display: flex;
      gap: 2px;
      justify-content: center;
      margin-top: auto;
      position: relative;
      width: 100%;

      .end-call-button {
        @include mixins.body2-bold();
        background-color: #ea355e;
        border: none;
        border-radius: 10px 0 0 10px;
        color: white;
        cursor: pointer;
        flex: 1;
        height: utils.in_rem(36px);
        padding: 0 utils.in_rem(12px);
        text-align: center;

        &:hover {
          background-color: darken(#ea355e, 5%);
        }

        &:active {
          transform: scale(0.98);
        }
      }

      .voicemail-button {
        align-items: center;
        background-color: #ea355e;
        border: none;
        border-radius: 0 10px 10px 0;
        color: white;
        cursor: pointer;
        display: flex;
        justify-content: center;
        height: utils.in_rem(36px);
        padding: 0 utils.in_rem(12px);
        @include mixins.body2-bold();

        &:hover:not(:disabled) {
          background-color: darken(#ea355e, 5%);
        }

        &:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
      }

      .voicemail-dropdown {
        backdrop-filter: blur(2rem);
        background-color: rgba(28, 28, 37, 0.75);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 5px;
        bottom: 100%;
        box-shadow: 0 1px 1rem rgba(0, 0, 0, 0.2);
        color: white;
        font-size: 0.8rem;
        font-weight: 200;
        left: auto;
        margin-bottom: 0.5rem;
        max-height: 10rem;
        overflow-y: auto;
        position: absolute;
        right: 0;
        width: 15rem;
        z-index: 1000;

        .voicemail-option {
          background-color: transparent;
          border: 0;
          border-radius: 5px;
          color: white;
          cursor: pointer;
          display: block;
          padding: 0.5rem 0.75rem;
          text-align: left;
          transition: background-color 0.15s ease;
          width: 100%;

          &:not(:last-child) {
            margin-bottom: 0.5rem;
          }

          &:first-child {
            font-style: italic;
            opacity: 0.8;
            cursor: default;
            pointer-events: none;
          }

          &:not(:first-child):hover {
            background-color: rgba(255, 255, 255, 0.1);
          }

          &:focus {
            background-color: rgba(255, 255, 255, 0.1);
            outline: none;
          }
        }
      }
    }
  }
</style>

<script>
  import { derived, get } from "svelte/store";
  import Button from "../../../../../../svelte-design-system/components/atoms/Button.svelte";
  import { showToast } from "../../../../../assets/js/src/general/utils";
  import { userStore } from "../../../../api-stores/user-store";
  import { CALL_ACTIONS, modifyCall } from "../../../../api/dialler";
  import { attendeesState } from "../attendeesState.svelte";
  import { diallerState } from "../diallerState.svelte";
  import { twilioManager } from "../utils/TwilioManager";
  import AudioDeviceDropdown from "./AudioDeviceDropdown.svelte";
  import CallActionButton from "./CallActionButton.svelte";

  const activeCall = $derived(diallerState.activeCall);
  const isRecording = $derived(diallerState.isRecording);
  const isMuted = $derived(diallerState.isMuted);
  const callId = $derived(diallerState.callId);
  const callHasRecording = $derived(diallerState.callHasRecording);

  userStore.once();

  const user = $derived($userStore);
  const isRecordingLocked = $derived(
    user?.diallerDefaultRecordingLock || false
  );
  const todoId = $derived(diallerState.todoId);
  const phoneNumber = $derived(attendeesState.candidate?.phone);

  const voicemailsStore = diallerState.voicemailsStore;
  const voicemailOptionsStore = derived(
    [voicemailsStore, voicemailsStore.meta],
    ([$voicemails, $meta]) => {
      if (
        $meta.fetching ||
        !$voicemails?.items ||
        !Array.isArray($voicemails.items)
      ) {
        return [];
      }
      return $voicemails.items.map((vm) => ({
        id: vm.voicemailId || vm.id,
        label: vm.name,
        duration: vm.duration,
        description: vm.duration ? `${Math.round(vm.duration)}s` : undefined,
      }));
    }
  );

  const voicemailOptions = $derived($voicemailOptionsStore);
  const hasVoicemails = $derived(voicemailOptions.length > 0);

  let showVoicemailDropdown = $state(false);
  let isDroppingVoicemail = $state(false);
  const handleVoicemailSelect = async (voicemailId) => {
    if (!callId || isDroppingVoicemail || !voicemailId) return;
    showVoicemailDropdown = false;
    isDroppingVoicemail = true;
    try {
      await modifyCall({
        callSid: callId,
        action: CALL_ACTIONS.DROP_VOICEMAIL,
        voicemailId,
        direction: diallerState.callDirection,
      });
      showToast({
        title: "Your voicemail was dropped successfully!",
        type: "success",
      });
      // After dropping voicemail, the call will end automatically when the voicemail finishes playing
      // We don't set state to "ended" here as Twilio will trigger the disconnect event
      // which will properly handle the call end flow including showing the outcome modal
      // The call will automatically disconnect after voicemail plays
      // TwilioManager.handleCallEnded() will handle showing the outcome modal
    } catch {
      showToast({
        title: "Failed to drop voicemail",
        subtitle: "Please try again",
        type: "danger",
      });
    } finally {
      isDroppingVoicemail = false;
    }
  };

  const toggleVoicemailDropdown = async () => {
    if (!activeCall || isDroppingVoicemail) return;
    if (!hasVoicemails) {
      await diallerState.voicemailsStore.get();
      const freshVoicemails = get(diallerState.voicemailsStore);
      if (!freshVoicemails?.items || freshVoicemails.items.length === 0) {
        showToast({
          title: "No voicemails available",
          subtitle: "Please add voicemails in settings",
          type: "warning",
        });
        return;
      }
    }
    showVoicemailDropdown = !showVoicemailDropdown;
  };

  const handleStartCall = async () => {
    const participants = attendeesState.attendees;
    diallerState.makeCall(participants, undefined);
  };

  const handleEndCall = async () => {
    await diallerState.endCall();
  };

  const handleRecord = async () => {
    if (user?.diallerDefaultRecordingLock) {
      showToast({
        title: "Your admin has blocked users from changing recording settings",
        type: "warning",
        timeout: 3000,
      });
      return;
    }
    try {
      await (isRecording
        ? twilioManager.pauseRecording()
        : diallerState.callHasRecording
          ? twilioManager.resumeRecording()
          : twilioManager.startRecording());
    } catch {
      showToast({
        title: "Recording failed",
        subtitle: "Unable to toggle recording",
        type: "danger",
      });
    }
  };

  const handleKeypad = () => {
    diallerState.viewMode = "keypad";
  };

  const handleOutcome = () => {
    diallerState.viewMode = "outcome";
  };

  const handleMute = async () => {
    try {
      await twilioManager.toggleMute();
    } catch {
      showToast({
        title: "Mute failed",
        subtitle: "Unable to change mute state",
        type: "danger",
      });
    }
  };

  const hasListenCoach = $derived(user?.listenCoach);

  const handleAddTeammate = () => {
    diallerState.viewMode = "addTeammate";
  };

  const { meta } = userStore;
  const buttonDisabled = $derived(!phoneNumber || !$meta.firstFetchDone);
</script>

<div class="dialler-controls">
  <div class="call-action-buttons">
    <CallActionButton
      type="record"
      active={isRecording}
      disabled={!activeCall || isRecordingLocked}
      onclick={handleRecord}
      testId="dialler-record-button"
      title={isRecording
        ? "Pause Recording"
        : callHasRecording
          ? "Resume Recording"
          : "Start Recording"}
    />

    <CallActionButton
      icon="fa-solid fa-grid-3"
      label="Keypad"
      disabled={!activeCall}
      onclick={handleKeypad}
      testId="dialler-keypad-button"
    />

    {#if !todoId}
      <CallActionButton
        icon="fa-solid fa-file-pen"
        label="Outcome"
        disabled={!activeCall}
        onclick={handleOutcome}
        testId="dialler-outcome-button"
      />
    {/if}

    {#if hasListenCoach && diallerState.callDirection !== "inbound"}
      <CallActionButton
        icon="fa-solid fa-plus"
        label="Add"
        disabled={!activeCall || !diallerState.ableToAddTeammate}
        onclick={handleAddTeammate}
        testId="dialler-add-teammate-button"
      />
    {/if}

    <CallActionButton
      type="mute"
      icon="fa-solid fa-microphone-slash"
      active={isMuted}
      disabled={!activeCall}
      onclick={handleMute}
      testId="dialler-mute-button"
    />

    <AudioDeviceDropdown />
  </div>

  <div class="end-call-section">
    {#if activeCall}
      <button
        class="end-call-button"
        onclick={handleEndCall}
        data-testid="dialler-end-call-button"
        type="button"
      >
        End Call
      </button>

      <button
        class="voicemail-button"
        disabled={!activeCall || !hasVoicemails || isDroppingVoicemail}
        onclick={toggleVoicemailDropdown}
        data-testid="dialler-voicemail-button"
        title={activeCall
          ? hasVoicemails
            ? "Drop Voicemail"
            : "No voicemails available"
          : "Call must be active"}
        aria-label={activeCall
          ? hasVoicemails
            ? "Drop Voicemail"
            : "No voicemails available"
          : "Call must be active"}
        type="button"
      >
        <i class="fa-solid fa-voicemail"></i>
      </button>
    {:else}
      <Button
        onclick={handleStartCall}
        disabled={buttonDisabled}
        style="width: 100%;"
        kind={buttonDisabled ? "disabled" : "primary"}
      >
        Call
      </Button>
    {/if}

    {#if showVoicemailDropdown && activeCall}
      <div class="voicemail-dropdown">
        <button class="voicemail-option" disabled type="button">
          Drop a message after the beep
        </button>
        {#each voicemailOptions as voicemail}
          <button
            class="voicemail-option"
            onclick={() => handleVoicemailSelect(voicemail.id)}
            type="button"
          >
            {voicemail.label}
          </button>
        {/each}
      </div>
    {/if}
  </div>
</div>
